import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Plus, Package, Truck, Image as ImageIcon, X, Trash2, Hotel, Bus, Boxes, Sparkles, AlertTriangle, Check, Camera, ZoomIn } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { money, SIZES, PRODUCT_CATEGORIES, CATEGORY_FIELDS } from "@/lib/orderStatus";

let _rowKey = 0;
const nextKey = () => `row-${++_rowKey}`;

const PARCEL_PRESETS = [
  { key: "enveloppe", label: "Enveloppe", weight_g: 100, length_cm: 25, width_cm: 18, height_cm: 2 },
  { key: "petit", label: "Petit colis", weight_g: 250, length_cm: 20, width_cm: 15, height_cm: 5 },
  { key: "moyen", label: "Moyen", weight_g: 1000, length_cm: 30, width_cm: 25, height_cm: 15 },
  { key: "grand", label: "Grand", weight_g: 3000, length_cm: 45, width_cm: 35, height_cm: 25 },
];

export default function Stocks() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("produits");
  return (
    <div className="px-4 pt-6">
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1">{t("stocks.title")}</h1>
      <p className="text-sm text-[#5C574F] mb-5">{t("stocks.sub")}</p>
      <div className="flex bg-white rounded-2xl p-1 border border-black/5 mb-5">
        {[["produits", t("stocks.tabProducts"), Package], ["voyages", t("stocks.tabTrips"), Truck]].map(([m, label, Icon]) => (
          <button
            key={m}
            data-testid={`stocks-tab-${m}`}
            onClick={() => setTab(m)}
            className={`flex-1 h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              tab === m ? "bg-[#E85D04] text-white" : "text-[#5C574F]"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "produits" ? <Products /> : <Trips />}
    </div>
  );
}

function Products() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const { data: business } = useQuery({ queryKey: ["business"], queryFn: async () => (await api.get("/business")).data });
  const cur = business?.currency || "XOF";
  const isCA = business?.country === "CA";
  const missingWeight = isCA ? products.filter((p) => !p.weight_g) : [];
  const [open, setOpen] = useState(false);

  return (
    <div>
      {missingWeight.length > 0 && (
        <div className="rounded-2xl bg-[#FFBA08]/12 border border-[#FFBA08]/40 p-3.5 mb-4 flex gap-3" data-testid="weight-reminder-banner">
          <AlertTriangle className="h-5 w-5 text-[#B07d00] shrink-0 mt-0.5" />
          <div className="text-sm text-[#5C574F]">
            <p className="font-semibold text-[#B07d00]">{t("stocks.weightMissingTitle", { count: missingWeight.length })}</p>
            <p className="text-xs mt-0.5">{t("stocks.weightMissingHint")}</p>
          </div>
        </div>
      )}
      <button
        data-testid="add-product-btn"
        onClick={() => setOpen(true)}
        className="w-full h-14 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 mb-4 active:scale-95 transition-transform"
      >
        <Plus className="h-5 w-5" /> {t("stocks.addProduct")}
      </button>
      {products.length === 0 && <Empty icon={Package} text={t("stocks.noProducts")} />}
      <div className="space-y-3">
        {products.map((p, i) => (
          <motion.div
            key={p.product_id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="bg-white rounded-2xl border border-black/5 shadow-sm p-3 flex gap-3"
            data-testid={`product-${p.product_id}`}
          >
            <div className="h-16 w-16 rounded-lg bg-[#F9F6F0] overflow-hidden flex items-center justify-center shrink-0">
              {p.image_url ? <img src={fileUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-[#cfc8ba]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold truncate">{p.name}</p>
                {p.published === false && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-[#9c968a]/15 text-[#5C574F] shrink-0" data-testid={`unpublished-${p.product_id}`}>{t("stocks.unpublished")}</span>}
                {isCA && !p.weight_g && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-[#FFBA08]/20 text-[#B07d00] shrink-0" data-testid={`no-weight-${p.product_id}`}>{t("stocks.weightMissingBadge")}</span>}
              </div>
              <p className="text-sm text-[#E85D04] font-bold">{money(p.price, cur)}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.stock_qty <= 3 ? "bg-[#D00000]/10 text-[#D00000]" : "bg-[#2D6A4F]/10 text-[#2D6A4F]"}`}>
                  {t("stocks.stockLabel")}: {p.stock_qty}
                </span>
                <span className="text-xs text-[#9c968a]">{t("stocks.cost")}: {money(p.cost_price, cur)}</span>
              </div>
            </div>
            <button
              data-testid={`delete-product-${p.product_id}`}
              onClick={async () => { await api.delete(`/products/${p.product_id}`); qc.invalidateQueries({ queryKey: ["products"] }); toast.success(t("stocks.deleted")); }}
              className="h-9 w-9 rounded-lg bg-[#D00000]/10 text-[#D00000] flex items-center justify-center self-center"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </motion.div>
        ))}
      </div>
      <AnimatePresence>{open && <ProductModal country={business?.country} onClose={() => setOpen(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ["products"] }); setOpen(false); }} />}</AnimatePresence>
    </div>
  );
}

function ProductModal({ onClose, onSaved, country }) {
  const { t } = useTranslation();
  const isCA = country === "CA";
  const [form, setForm] = useState({ name: "", description: "", price: "", stock_qty: "", cost_price: "", photos: [], sizes: [], size_stock: {}, category: "", attributes: {}, published: true, weight_g: "", length_cm: "", width_cm: "", height_cm: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoQc, setPhotoQc] = useState(null);
  const [showDims, setShowDims] = useState(isCA);
  const [labelB64, setLabelB64] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [cleaningIdx, setCleaningIdx] = useState(-1);
  const [compare, setCompare] = useState(null);
  const [packshotStyle, setPackshotStyle] = useState("floating");
  const [customBg, setCustomBg] = useState("");
  const [presentation, setPresentation] = useState("floating");
  const [background, setBackground] = useState("studio");
  const [showPhotoSource, setShowPhotoSource] = useState(false);
  const [zoom, setZoom] = useState(null);
  const fileRef = useRef();
  const cameraRef = useRef();
  const labelRef = useRef();
  const labelCameraRef = useRef();

  const toB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
  const articleB64 = form.photos[0]?.b64 || "";

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.photos.length >= 3) { toast.error(t("stocks.max3photos")); return; }
    setUploading(true);
    try {
      const b64 = await toB64(file);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...f.photos, { raw: data.url, clean: "", b64 }] }));
      setPhotoQc(data.quality || null);
      if (data.quality && !data.quality.ok) toast.warning(t("stocks.photoQualityWarn"));
      else toast.success(t("stocks.photoQualityOk"));
    } catch (err) {
      toast.error(t("stocks.uploadFailed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const uploadLabel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLabelB64(await toB64(file));
    toast.success(t("stocks.labelAdded"));
  };

  const runAI = async () => {
    if (!articleB64) { toast.error(t("stocks.addArticlePhotoFirst")); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/product-fiche", { image_base64: articleB64, label_base64: labelB64 || null, country });
      const attrs = data.attributes || {};
      const desc = data.description + (attrs.matiere || attrs.taille || attrs.marque ? `\n\nMatière : ${attrs.matiere || "—"} · Taille : ${attrs.taille || "—"}${attrs.marque ? ` · Marque : ${attrs.marque}` : ""}` : "");
      setForm((f) => ({ ...f, name: data.name || f.name, description: desc || f.description }));
      toast.success(t("stocks.ficheGenerated"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("stocks.ficheFailed"));
    } finally {
      setAiBusy(false);
    }
  };

  const cleanPhoto = async (idx) => {
    const ph = form.photos[idx];
    if (!ph?.b64) { toast.error(t("stocks.photoNotFound")); return; }
    if (!isFashionCat && packshotStyle === "custom" && !customBg.trim()) { toast.error(t("stocks.customRequired")); return; }
    setCleaningIdx(idx);
    try {
      const payload = isFashionCat
        ? { image_base64: ph.b64, category: form.category, packshot: presentation, background }
        : { image_base64: ph.b64, category: form.category, packshot: packshotStyle, custom_bg: customBg };
      const { data } = await api.post("/ai/mannequin", payload);
      setCompare({ idx, candidate: data.image });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("stocks.cleanFailed"));
    } finally {
      setCleaningIdx(-1);
    }
  };

  const applyClean = () => {
    if (!compare) return;
    const { idx, candidate } = compare;
    setForm((f) => ({ ...f, photos: f.photos.map((p, i) => (i === idx ? { ...p, clean: candidate } : p)) }));
    setCompare(null);
    toast.success(t("stocks.cleaned2"));
  };
  const rejectClean = () => setCompare(null);

  const toggleSize = (code) => setForm((f) => {
    const on = f.sizes.includes(code);
    const sizes = on ? f.sizes.filter((x) => x !== code) : [...f.sizes, code];
    const size_stock = { ...f.size_stock };
    if (on) delete size_stock[code];
    else if (size_stock[code] == null) size_stock[code] = 0;
    return { ...f, sizes, size_stock };
  });
  const setSizeStock = (code, val) => setForm((f) => ({ ...f, size_stock: { ...f.size_stock, [code]: Math.max(0, parseInt(val || "0", 10) || 0) } }));
  const totalStock = form.sizes.length ? form.sizes.reduce((s, c) => s + (parseInt(form.size_stock[c], 10) || 0), 0) : (parseInt(form.stock_qty, 10) || 0);
  const catCfg = CATEGORY_FIELDS[form.category] || null;
  const showSizes = !!catCfg?.sizes;
  const pickCategory = (label) => {
    setPackshotStyle(label === "Mode & Accessoires" ? "floating" : "studio");
    setForm((f) => {
      const cfg = CATEGORY_FIELDS[label];
      const next = { ...f, category: label, attributes: {} };
      if (!cfg?.sizes) { next.sizes = []; next.size_stock = {}; }
      return next;
    });
  };
  const isFashionCat = form.category === "Mode & Accessoires";
  // Mode & Accessoires : 2 choix combinés (présentation du vêtement + ambiance de fond)
  const PRESENTATION_OPTIONS = [
    { v: "floating", k: "presFloating" }, { v: "ghost", k: "presGhost" }, { v: "flat", k: "presFlat" },
    { v: "model", k: "presModel" }, { v: "lifestyle", k: "presLifestyle" },
  ];
  const AMBIANCE_OPTIONS = [
    { v: "studio", k: "ambStudio" }, { v: "wood", k: "ambWood" }, { v: "urban", k: "ambUrban" },
    { v: "sun", k: "ambSun" }, { v: "luxe", k: "ambLuxe" },
  ];
  // Autres catégories : inchangé
  const STYLE_OPTIONS = [
    { v: "studio", k: "styleStudio" }, { v: "nature", k: "styleNature" }, { v: "luxe", k: "styleLuxe" }, { v: "custom", k: "styleCustom" },
  ];
  const setAttr = (key, val) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [key]: val } }));

  const save = async () => {
    if (!form.name || !form.price) { toast.error(t("stocks.namePriceReq")); return; }
    if (!form.category) { toast.error(t("stocks.categoryReq")); return; }
    if (isCA && (!form.weight_g || !form.length_cm || !form.width_cm || !form.height_cm)) { toast.error(t("stocks.weightDimsMandatory")); return; }
    setBusy(true);
    try {
      await api.post("/products", {
        name: form.name, description: form.description, price: +form.price,
        stock_qty: +form.stock_qty || 0, cost_price: +form.cost_price || 0,
        photos: form.photos.map((p) => ({ raw: p.raw, clean: p.clean })),
        category: form.category, published: form.published, attributes: form.attributes,
        sizes: form.sizes, size_stock: form.size_stock,
        weight_g: +form.weight_g || 0, length_cm: +form.length_cm || 0, width_cm: +form.width_cm || 0, height_cm: +form.height_cm || 0,
      });
      toast.success(form.published ? t("stocks.addedPublished") : t("stocks.savedStock"));
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      {compare && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="compare-modal" onClick={(e) => e.stopPropagation()}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-5 w-full max-w-md">
            <h3 className="font-display text-lg font-bold flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#E85D04]" /> {t("stocks.comparTitle")}</h3>
            <p className="text-xs text-[#5C574F] mt-1 mb-3">{t("stocks.comparHint")}</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-[10px] font-bold text-[#9c968a] mb-1 uppercase tracking-wide">{t("stocks.comparOriginal")}</p>
                <div className="aspect-square rounded-xl border border-[#E5E0D8] bg-[#F9F6F0] overflow-hidden">
                  <img src={fileUrl(form.photos[compare.idx]?.raw)} alt="" className="h-full w-full object-contain cursor-zoom-in" onClick={() => setZoom(fileUrl(form.photos[compare.idx]?.raw))} data-testid="compare-original-img" />
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#2D6A4F] mb-1 uppercase tracking-wide">{t("stocks.comparCleaned")}</p>
                <div className="aspect-square rounded-xl border-2 border-[#2D6A4F] bg-[#F9F6F0] overflow-hidden">
                  <img src={fileUrl(compare.candidate)} alt="" className="h-full w-full object-contain cursor-zoom-in" onClick={() => setZoom(fileUrl(compare.candidate))} data-testid="compare-cleaned-img" />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" data-testid="compare-keep-original" onClick={rejectClean} className="flex-1 h-11 rounded-xl border border-[#E5E0D8] font-bold text-sm text-[#5C574F]">{t("stocks.comparKeep")}</button>
              <button type="button" data-testid="compare-use-cleaned" onClick={applyClean} className="flex-1 h-11 rounded-xl bg-[#2D6A4F] text-white font-bold text-sm flex items-center justify-center gap-1"><Check className="h-4 w-4" /> {t("stocks.comparUse")}</button>
            </div>
          </motion.div>
        </div>
      )}
      {zoom && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" data-testid="photo-zoom-modal" onClick={(e) => { e.stopPropagation(); setZoom(null); }}>
          <button type="button" data-testid="photo-zoom-close" className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
          <img src={zoom} alt="" className="max-h-[90vh] max-w-full object-contain rounded-xl" />
        </div>
      )}
      {showPhotoSource && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="photo-source-modal" onClick={(e) => { e.stopPropagation(); setShowPhotoSource(false); }}>
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5 space-y-3">
            <h3 className="font-display text-lg font-bold text-center">{t("stocks.photoSourceTitle")}</h3>
            <button type="button" data-testid="photo-source-camera" onClick={() => { const tgt = showPhotoSource; setShowPhotoSource(false); (tgt === "label" ? labelCameraRef : cameraRef).current?.click(); }} className="w-full h-14 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2">
              <Camera className="h-5 w-5" /> {t("stocks.photoSourceCamera")}
            </button>
            <button type="button" data-testid="photo-source-gallery" onClick={() => { const tgt = showPhotoSource; setShowPhotoSource(false); (tgt === "label" ? labelRef : fileRef).current?.click(); }} className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold flex items-center justify-center gap-2">
              <ImageIcon className="h-5 w-5" /> {t("stocks.photoSourceGallery")}
            </button>
            <button type="button" data-testid="photo-source-cancel" onClick={() => setShowPhotoSource(false)} className="w-full h-11 rounded-xl border border-[#E5E0D8] text-[#5C574F] font-semibold text-sm">{t("common.cancel")}</button>
          </motion.div>
        </div>
      )}
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto no-scrollbar"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{t("stocks.newProduct")}</h3>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        {/* Photos (jusqu'à 3, chacune nettoyable par l'IA) */}
        <div className="rounded-xl p-3 mb-4 border border-[#E5E0D8] bg-[#FAF8F4]" data-testid="photos-panel">
          <p className="font-display font-bold text-sm flex items-center gap-1.5 text-[#1B4332]"><Sparkles className="h-4 w-4 text-[#E85D04]" /> {t("stocks.photosTitle")}</p>
          <p className="text-xs text-[#5C574F] mt-1 mb-2">{t("stocks.photosHint")}</p>
          {isFashionCat ? (
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <label className="flex items-center gap-2">
                <span className="text-xs text-[#5C574F]">{t("stocks.presentationLabel")}</span>
                <select data-testid="presentation-select" value={presentation} onChange={(e) => setPresentation(e.target.value)} className="h-9 rounded-lg border border-[#E5E0D8] bg-white px-2 text-sm">
                  {PRESENTATION_OPTIONS.map((o) => <option key={o.v} value={o.v}>{t(`stocks.${o.k}`)}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <span className="text-xs text-[#5C574F]">{t("stocks.ambianceLabel")}</span>
                <select data-testid="ambiance-select" value={background} onChange={(e) => setBackground(e.target.value)} className="h-9 rounded-lg border border-[#E5E0D8] bg-white px-2 text-sm">
                  {AMBIANCE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{t(`stocks.${o.k}`)}</option>)}
                </select>
              </label>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-[#5C574F]">{t("stocks.style")}</span>
                <select data-testid="packshot-style" value={packshotStyle} onChange={(e) => setPackshotStyle(e.target.value)} className="h-9 rounded-lg border border-[#E5E0D8] bg-white px-2 text-sm">
                  {STYLE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{t(`stocks.${o.k}`)}</option>)}
                </select>
              </div>
              {packshotStyle === "custom" && (
                <input
                  data-testid="custom-bg-input"
                  value={customBg}
                  onChange={(e) => setCustomBg(e.target.value)}
                  placeholder={t("stocks.customPlaceholder")}
                  className="w-full h-9 rounded-lg border border-[#E5E0D8] bg-white px-2 text-sm mb-3"
                />
              )}
            </>
          )}
          <div className="grid grid-cols-3 gap-2">
            {form.photos.map((ph, i) => {
              const src = ph.clean || ph.raw;
              const cleaning = cleaningIdx === i;
              return (
                <div key={ph.raw || ph.clean} className="rounded-xl border border-[#E5E0D8] bg-white overflow-hidden" data-testid={`product-photo-${i}`}>
                  <div className="relative aspect-square bg-[#F9F6F0]">
                    <img src={fileUrl(src)} alt="" className="h-full w-full object-contain cursor-zoom-in" onClick={() => setZoom(fileUrl(src))} data-testid={`zoom-photo-${i}`} />
                    <span className="absolute top-1 right-8 h-6 w-6 rounded-full bg-black/50 text-white flex items-center justify-center pointer-events-none"><ZoomIn className="h-3.5 w-3.5" /></span>
                    {i === 0 && <span className="absolute top-1 left-1 bg-black/70 text-white text-[8px] px-1.5 py-0.5 rounded">{t("stocks.main")}</span>}
                    <span className={`absolute bottom-1 left-1 text-[8px] px-1.5 py-0.5 rounded text-white ${ph.clean ? "bg-[#2D6A4F]" : "bg-[#9c968a]"}`} data-testid={`photo-status-${i}`}>{ph.clean ? t("stocks.cleaned") : t("stocks.raw")}</span>
                    <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <button type="button" data-testid={`clean-photo-${i}`} onClick={() => cleanPhoto(i)} disabled={cleaningIdx >= 0 || !ph.b64} className="w-full h-8 bg-[#E85D04] text-white text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50">
                    {cleaning ? t("stocks.cleaning") : <><Sparkles className="h-3 w-3" /> {ph.clean ? t("stocks.reclean") : t("stocks.clean")}</>}
                  </button>
                </div>
              );
            })}
            {form.photos.length < 3 && (
              <button
                type="button"
                data-testid="upload-image-btn"
                onClick={() => setShowPhotoSource("product")}
                className="aspect-square rounded-xl border-2 border-dashed border-[#E5E0D8] flex flex-col items-center justify-center text-[#9c968a] overflow-hidden"
              >
                {uploading ? <span className="text-xs">{t("stocks.sending")}</span> : <><ImageIcon className="h-6 w-6 mb-1" /><span className="text-[11px]">{t("stocks.add")}</span></>}
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="image-file-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="image-camera-input" />
        </div>

        {/* Assistant IA Business Partner */}
        <div className="rounded-xl p-3 mb-4 bg-gradient-to-br from-[#1B4332] to-[#2D6A4F] text-white" data-testid="ai-assistant-panel">
          <p className="font-display font-bold text-sm flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#FFBA08]" /> {t("stocks.aiTitle")}</p>
          <p className="text-xs text-white/80 mt-1 mb-2">{t("stocks.aiHint")}</p>
          <div className="flex items-center gap-2">
            <button data-testid="upload-label-btn" onClick={() => setShowPhotoSource("label")} className="flex-1 h-10 rounded-lg bg-white/15 text-white text-xs font-semibold flex items-center justify-center gap-1.5">
              <ImageIcon className="h-4 w-4" /> {labelB64 ? t("stocks.labelOk") : t("stocks.labelPhoto")}
            </button>
            <button data-testid="run-ai-btn" onClick={runAI} disabled={aiBusy || !articleB64} className="flex-1 h-10 rounded-lg bg-[#FFBA08] text-[#1A1A1A] text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
              {aiBusy ? t("stocks.analyzing") : <><Sparkles className="h-4 w-4" /> {t("stocks.genFiche")}</>}
            </button>
          </div>
          <input ref={labelRef} type="file" accept="image/*" className="hidden" onChange={uploadLabel} data-testid="label-file-input" />
          <input ref={labelCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadLabel} data-testid="label-camera-input" />
        </div>

        {photoQc && (
          <div data-testid="photo-qc" className={`rounded-xl p-3 mb-4 text-sm ${photoQc.ok ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-[#FFBA08]/15 text-[#7a5800]"}`}>
            <p className="font-semibold">{t("stocks.qcScore", { score: photoQc.score })} {photoQc.ok ? "✓" : "⚠️"}</p>
            {photoQc.issues?.length > 0 && (
              <ul className="list-disc list-inside mt-1 text-xs">
                {photoQc.issues.map((it) => <li key={it}>{it}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="space-y-3">
          <Input testid="product-name" label={t("stocks.nameLabel")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder={t("stocks.namePh")} />
          <Input testid="product-desc" label={t("stocks.descLabel")} value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder={t("stocks.descPh")} />
          <div>
            <label className="block text-sm font-semibold text-[#5C574F] mb-1">{t("stocks.categoryLabel")} <span className="text-[#E85D04]">*</span></label>
            <select
              data-testid="product-category"
              value={form.category}
              onChange={(e) => pickCategory(e.target.value)}
              className="w-full h-12 bg-[#F9F6F0] rounded-xl px-4 outline-none border border-transparent focus:border-[#E85D04]"
            >
              <option value="">{t("stocks.chooseCategory")}</option>
              {PRODUCT_CATEGORIES.map((c) => <option key={c.key} value={c.label}>{c.label}</option>)}
            </select>
          </div>
          {catCfg && catCfg.fields.length > 0 && (
            <div data-testid="category-fields" className="rounded-xl border border-[#E5E0D8] bg-[#FAF8F4] p-3 space-y-2">
              <p className="text-sm font-semibold text-[#1B4332]">{t("stocks.characteristics")} {form.category}</p>
              <div className="grid grid-cols-2 gap-2">
                {catCfg.fields.map((fld) => (
                  <div key={fld.key} className={fld.type === "select" || fld.key === "dimensions" ? "col-span-2" : ""}>
                    <label className="block text-xs font-semibold text-[#5C574F] mb-1">{fld.label}</label>
                    {fld.type === "select" ? (
                      <select
                        data-testid={`attr-${fld.key}`}
                        value={form.attributes[fld.key] || ""}
                        onChange={(e) => setAttr(fld.key, e.target.value)}
                        className="w-full h-10 bg-white rounded-lg px-2 text-sm border border-[#E5E0D8] outline-none focus:border-[#E85D04]"
                      >
                        <option value="">—</option>
                        {fld.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={fld.type === "date" ? "date" : "text"}
                        data-testid={`attr-${fld.key}`}
                        value={form.attributes[fld.key] || ""}
                        onChange={(e) => setAttr(fld.key, e.target.value)}
                        className="w-full h-10 bg-white rounded-lg px-2.5 text-sm border border-[#E5E0D8] outline-none focus:border-[#E85D04]"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {showSizes && (
          <div data-testid="sizes-section">
            <label className="block text-sm font-semibold text-[#5C574F] mb-1">{t("stocks.sizesLabel")} <span className="text-[#9c968a] font-normal">{t("stocks.sizesClothing")}</span></label>
            <p className="text-xs text-[#9c968a] mb-2">{t("stocks.sizesHint")}</p>
            <div className="flex flex-col gap-1.5">
              {SIZES.map((s) => {
                const on = form.sizes.includes(s.code);
                return (
                  <div key={s.code} className={`w-full rounded-xl px-3 py-2.5 border-2 transition-colors flex items-center justify-between gap-2 ${on ? "border-[#1B4332] bg-[#1B4332]/5" : "border-[#E5E0D8] bg-[#F9F6F0]"}`}>
                    <button
                      type="button"
                      data-testid={`size-toggle-${s.code}`}
                      onClick={() => toggleSize(s.code)}
                      className="text-left flex-1"
                    >
                      <span className="text-sm font-semibold text-[#1A1A1A]">{s.code} </span>
                      <span className="text-xs text-[#9c968a]">({s.en})</span>
                    </button>
                    {on && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-xs text-[#9c968a]">{t("stocks.qty")}</span>
                        <input
                          type="number" min="0"
                          data-testid={`size-stock-${s.code}`}
                          value={form.size_stock[s.code] ?? 0}
                          onChange={(e) => setSizeStock(s.code, e.target.value)}
                          className="w-16 h-9 rounded-lg border border-[#E5E0D8] bg-white px-2 text-sm text-center"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {form.sizes.length > 0 && <p className="text-xs font-semibold text-[#1B4332] mt-2" data-testid="total-stock">{t("stocks.totalStock", { count: totalStock })}</p>}
          </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input testid="product-price" label={t("stocks.priceLabel")} type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
            <Input testid="product-cost" label={t("stocks.costLabel")} type="number" value={form.cost_price} onChange={(v) => setForm({ ...form, cost_price: v })} />
          </div>
          {form.sizes.length === 0 && (
            <Input testid="product-stock" label={t("stocks.stockQtyLabel")} type="number" value={form.stock_qty} onChange={(v) => setForm({ ...form, stock_qty: v })} />
          )}
          <div>
            {isCA ? (
              <>
                <p className="text-sm font-semibold text-[#5C574F] mb-1">{t("stocks.weightDimsReq")} <span className="text-[#E85D04]">*</span></p>
                <p className="text-xs text-[#9c968a] mb-2">{t("stocks.weightDimsReqHint")}</p>
              </>
            ) : (
              <button
                type="button"
                data-testid="toggle-dims-btn"
                onClick={() => setShowDims((v) => !v)}
                className="text-sm font-semibold text-[#E85D04] mb-2 flex items-center gap-1"
              >
                {showDims ? t("stocks.hideDims") : t("stocks.addDims")} {t("stocks.weightDimsOpt")} <span className="text-[#9c968a] font-normal">{t("stocks.optional")}</span>
              </button>
            )}
            {showDims && (
              <>
                <div className="flex flex-wrap gap-2 mb-3" data-testid="parcel-presets">
                  {PARCEL_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      data-testid={`preset-${p.key}`}
                      onClick={() => setForm((f) => ({ ...f, weight_g: String(p.weight_g), length_cm: String(p.length_cm), width_cm: String(p.width_cm), height_cm: String(p.height_cm) }))}
                      className="px-3 py-1.5 rounded-full bg-[#F9F6F0] border border-[#E5E0D8] text-xs font-semibold text-[#5C574F] hover:border-[#E85D04] hover:text-[#E85D04] transition-colors"
                    >
                      {p.label} <span className="text-[#9c968a] font-normal">· {p.weight_g}g · {p.length_cm}×{p.width_cm}×{p.height_cm}</span>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <Input testid="product-weight" label={t("stocks.weightG")} type="number" value={form.weight_g} onChange={(v) => setForm({ ...form, weight_g: v })} />
                  <Input testid="product-length" label={t("stocks.lengthCm")} type="number" value={form.length_cm} onChange={(v) => setForm({ ...form, length_cm: v })} />
                  <Input testid="product-width" label={t("stocks.widthCm")} type="number" value={form.width_cm} onChange={(v) => setForm({ ...form, width_cm: v })} />
                  <Input testid="product-height" label={t("stocks.heightCm")} type="number" value={form.height_cm} onChange={(v) => setForm({ ...form, height_cm: v })} />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-[#E5E0D8] bg-[#F9F6F0] px-4 py-3 mt-4" data-testid="publish-toggle-row">
          <div>
            <p className="text-sm font-semibold text-[#1A1A1A]">{t("stocks.publishStore")}</p>
            <p className="text-xs text-[#9c968a]">{form.published ? t("stocks.publishOn") : t("stocks.publishOff")}</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.published}
            data-testid="publish-toggle"
            onClick={() => setForm((f) => ({ ...f, published: !f.published }))}
            className={`relative h-7 w-12 rounded-full transition-colors shrink-0 ${form.published ? "bg-[#2D6A4F]" : "bg-[#cfc8ba]"}`}
          >
            <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${form.published ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
        <button data-testid="save-product-btn" onClick={save} disabled={busy} className="w-full h-14 mt-4 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60">
          {busy ? t("common.saving") : (form.published ? t("stocks.savePublish") : t("stocks.saveStock"))}
        </button>
      </motion.div>
    </motion.div>
  );
}

function Trips() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { data: trips = [] } = useQuery({ queryKey: ["trips"], queryFn: async () => (await api.get("/purchase-trips")).data });
  const { data: business } = useQuery({ queryKey: ["business"], queryFn: async () => (await api.get("/business")).data });
  const cur = business?.currency || "XOF";
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        data-testid="add-trip-btn"
        onClick={() => setOpen(true)}
        className="w-full h-14 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 mb-4 active:scale-95 transition-transform"
      >
        <Plus className="h-5 w-5" /> {t("stocks.newTrip")}
      </button>
      {trips.length === 0 && <Empty icon={Truck} text={t("stocks.noTrips")} />}
      <div className="space-y-3">
        {trips.map((tr) => (
          <div key={tr.trip_id} className="bg-white rounded-2xl border border-black/5 shadow-sm p-4" data-testid={`trip-${tr.trip_id}`}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-semibold">{tr.label}</h4>
              <span className="font-bold text-[#E85D04]">{money(tr.total_cost, cur)}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2 text-xs">
              <Tag icon={Boxes} text={`${t("stocks.merchandise")} ${money(tr.merchandise_total, cur)}`} />
              <Tag icon={Bus} text={`${t("stocks.transport")} ${money(tr.transport_cost, cur)}`} />
              <Tag icon={Hotel} text={`${t("stocks.hotel")} ${money(tr.hotel_cost, cur)}`} />
            </div>
            <div className="mt-3 border-t border-[#F0ece4] pt-2 space-y-1">
              {tr.items.map((it, i) => (
                <div key={`${it.product_id || it.name}-${i}`} className="flex justify-between text-sm">
                  <span className="text-[#5C574F]">{it.name} ×{it.qty}</span>
                  <span className="font-medium">{t("stocks.returnLabel")} {money(it.unit_cost_revenue, cur)}{t("stocks.perUnit")}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <AnimatePresence>{open && <TripModal onClose={() => setOpen(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ["trips"] }); qc.invalidateQueries({ queryKey: ["products"] }); setOpen(false); }} />}</AnimatePresence>
    </div>
  );
}

function TripModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const [form, setForm] = useState({ label: "", transport_cost: "", hotel_cost: "", other_cost: "" });
  const [items, setItems] = useState([{ _k: nextKey(), product_id: "", name: "", qty: "", unit_purchase_cost: "" }]);
  const [busy, setBusy] = useState(false);

  const addItem = () => setItems([...items, { _k: nextKey(), product_id: "", name: "", qty: "", unit_purchase_cost: "" }]);
  const updItem = (i, k, v) => {
    const copy = [...items];
    copy[i][k] = v;
    if (k === "product_id" && v) {
      const p = products.find((x) => x.product_id === v);
      if (p) copy[i].name = p.name;
    }
    setItems(copy);
  };

  const save = async () => {
    const validItems = items.filter((it) => it.name && +it.qty > 0 && +it.unit_purchase_cost > 0).map((it) => ({
      product_id: it.product_id || null, name: it.name, qty: +it.qty, unit_purchase_cost: +it.unit_purchase_cost,
    }));
    if (!form.label || validItems.length === 0) { toast.error(t("stocks.addLabelItem")); return; }
    setBusy(true);
    try {
      await api.post("/purchase-trips", {
        label: form.label, transport_cost: +form.transport_cost || 0,
        hotel_cost: +form.hotel_cost || 0, other_cost: +form.other_cost || 0, items: validItems,
      });
      toast.success(t("stocks.tripSaved"));
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{t("stocks.tripTitle")}</h3>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        <Input testid="trip-label" label={t("stocks.tripLabel")} value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder={t("stocks.tripLabelPh")} />
        <p className="text-sm font-semibold text-[#5C574F] mt-4 mb-2">{t("stocks.logisticsCosts")}</p>
        <div className="grid grid-cols-3 gap-2">
          <Input testid="trip-transport" label={t("stocks.transport")} type="number" value={form.transport_cost} onChange={(v) => setForm({ ...form, transport_cost: v })} />
          <Input testid="trip-hotel" label={t("stocks.hotel")} type="number" value={form.hotel_cost} onChange={(v) => setForm({ ...form, hotel_cost: v })} />
          <Input testid="trip-other" label={t("stocks.other")} type="number" value={form.other_cost} onChange={(v) => setForm({ ...form, other_cost: v })} />
        </div>
        <p className="text-sm font-semibold text-[#5C574F] mt-4 mb-2">{t("stocks.purchasedItems")}</p>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={it._k} className="bg-[#F9F6F0] rounded-xl p-3" data-testid={`trip-item-${i}`}>
              <select
                data-testid={`trip-item-select-${i}`}
                value={it.product_id}
                onChange={(e) => updItem(i, "product_id", e.target.value)}
                className="w-full h-11 bg-white rounded-lg px-3 text-sm mb-2 outline-none border border-[#E5E0D8]"
              >
                <option value="">{t("stocks.newItem")}</option>
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({t("stocks.stockLabel").toLowerCase()} {p.stock_qty})</option>)}
              </select>
              {!it.product_id && (
                <input
                  data-testid={`trip-item-name-${i}`}
                  value={it.name}
                  onChange={(e) => updItem(i, "name", e.target.value)}
                  placeholder={t("stocks.itemNamePh")}
                  className="w-full h-11 bg-white rounded-lg px-3 text-sm mb-2 outline-none border border-[#E5E0D8]"
                />
              )}
              <div className="grid grid-cols-2 gap-2">
                <input data-testid={`trip-item-qty-${i}`} type="number" value={it.qty} onChange={(e) => updItem(i, "qty", e.target.value)} placeholder={t("stocks.qtyPh")} className="h-11 bg-white rounded-lg px-3 text-sm outline-none border border-[#E5E0D8]" />
                <input data-testid={`trip-item-cost-${i}`} type="number" value={it.unit_purchase_cost} onChange={(e) => updItem(i, "unit_purchase_cost", e.target.value)} placeholder={t("stocks.purchasePricePh")} className="h-11 bg-white rounded-lg px-3 text-sm outline-none border border-[#E5E0D8]" />
              </div>
            </div>
          ))}
        </div>
        <button data-testid="add-trip-item-btn" onClick={addItem} className="w-full h-11 mt-3 rounded-xl border border-dashed border-[#E5E0D8] text-[#5C574F] font-medium text-sm">{t("stocks.addItem")}</button>
        <button data-testid="save-trip-btn" onClick={save} disabled={busy} className="w-full h-14 mt-4 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60">
          {busy ? t("common.saving") : t("stocks.saveTrip")}
        </button>
      </motion.div>
    </motion.div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="flex-1">
      <label className="text-xs font-semibold text-[#5C574F]">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 mt-1 outline-none border border-transparent focus:border-[#E85D04]"
      />
    </div>
  );
}

function Tag({ icon: Icon, text }) {
  return <span className="inline-flex items-center gap-1 bg-[#F9F6F0] px-2 py-1 rounded-full text-[#5C574F] font-medium"><Icon className="h-3 w-3" /> {text}</span>;
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 text-[#9c968a]">
      <Icon className="h-12 w-12 mx-auto mb-3 opacity-40" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
